import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { computeStorageBalance } from "./storageBalance";
import { useTranslation } from "react-i18next";
import { CheckboxOptions } from "../../../components/Input/CheckboxOptions";
import { ExtraLogo } from "./add-heating-fee-order-dialog";
import { Stepper } from "../../../components/Buttons/Stepper";
import { PriceTierNudge } from "../../../components/Logo/PriceTierNudge";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { enviroment } from "../../../../enviroment";

interface PartlyHeatingListProps {
  extraLogos: ExtraLogo[];
  setExtraLogos: (extraLogo: ExtraLogo) => void;
}

export function PartlyHeatingList({
  extraLogos,
  setExtraLogos,
}: PartlyHeatingListProps) {
  const { t } = useTranslation();
  const ref = React.createRef<HTMLDivElement>();

  // Admins can turn off ordering extra logos to storage (stock orders) —
  // globally or for the current user's organization (blocked customers), so we
  // gate on the per-user effective flag. Shares the cached logos/options/all
  // query with the rest of the app. Fail-open: while the flag is unknown
  // (loading/error) the choice stays visible, exactly as before — the API
  // degrades storage→delivery anyway.
  const { logoRepository } = useRepositoriesContext();
  const options = logoRepository.useOptions();
  const stockOrdersDisabled = options.data?.stockOrdersEffectiveDisabled === true;

  const scroll = (scrollOffset: number) => {
    ref.current!.scrollLeft += scrollOffset;
  };

  const heatingProducts = extraLogos
    // Currently, do not include name/number logos in the partly heating dialog.
    .filter((item) => !["NumberLogo", "NameLogo"].includes(item.logo.logoType))
    .map((item) => (
      <HeatingItem
        key={item.logo.id}
        item={item}
        setItem={setExtraLogos}
        stockOrdersDisabled={stockOrdersDisabled}
      />
    ));

  const hasMultiple = heatingProducts.length > 1;

  return (
    <div className="flex flex-col space-y-4">
      <b>{t("orderConfirmation.addExtraLogo")}</b>
      <div className="flex flex-row items-center justify-between">
        {hasMultiple && (
          <div className="flex-1 -ml-6">
            <button onClick={() => scroll(-250)} className="p-2">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          </div>
        )}
        <div className="flex-3 overflow-hidden max-w-3xl">
          <div ref={ref} className="flex overflow-x-scroll scroll-smooth pb-2">
            <div className="flex flex-nowrap gap-x-5">{heatingProducts}</div>
          </div>
        </div>
        {hasMultiple && (
          <div className="flex-1 -mr-8 flex justify-end">
            <button onClick={() => scroll(250)} className="p-2">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface HeatingItemProps {
  item: ExtraLogo;
  setItem: (HeatingPartlyOption: ExtraLogo) => void;
  stockOrdersDisabled: boolean;
}

function HeatingItem({ item, setItem, stockOrdersDisabled }: HeatingItemProps) {
  const { t } = useTranslation();
  return (
    <div className="inline-block">
      <div className="w-48 h-fit max-w-xs overflow-hidden rounded-lg bg-[#F8FAFD] hover:shadow-xl transition-shadow duration-300 ease-in-out">
        <div className="flex flex-col px-5 pb-3">
          <div className="h-full w-full min-h-32 min-w-32 my-3 flex justify-center items-center">
            <img
              className="object-contain"
              src={item.logo.displayImage.lowResUrl}
            />
          </div>
          <div className="flex flex-col justify-items-start italic">
            <div className="font-bold">
              {t("orderConfirmation.extraNoHeating")}
            </div>
            <div className="">{t("orderConfirmation.chooseAmount")}</div>
            <Stepper
              onValueChange={(v) => setItem({ ...item, quantity: v })}
              min={0}
              value={item.quantity}
            />
          </div>
          {!stockOrdersDisabled && (
            <div className="flex flex-row justify-between justify-center items-center italic">
              <p>{t("orderConfirmation.toStorage")}</p>
              <CheckboxOptions
                checkboxPosition="right"
                name="toStorage"
                checked={item.option === "toStorage"}
                onChange={(x) =>
                  x.target.checked && setItem({ ...item, option: "toStorage" })
                }
              ></CheckboxOptions>
            </div>
          )}
          <div className="flex flex-row justify-between justify-center items-center italic">
            <p>{t("orderConfirmation.toDelivery")}</p>
            <CheckboxOptions
              checkboxPosition="right"
              name="toDelivery"
              checked={item.option === "toDelivery"}
              onChange={(x) =>
                x.target.checked && setItem({ ...item, option: "toDelivery" })
              }
            ></CheckboxOptions>
          </div>
          <StorageBalance item={item} />
          <PriceTierNudge
            prices={item.logo.product?.prices}
            currentTotal={item.cartQuantity + item.quantity}
            onAddClick={(needed) =>
              setItem({ ...item, quantity: item.quantity + needed })
            }
          />
        </div>
      </div>
    </div>
  );
}

// Storage balance on the card (flag REACT_APP_STORAGE_BALANCE_ENABLED, case
// 20006106): the step used to show NOTHING about storage, so a customer with
// 107 in stock read the price-tier line as "no stock". Shows the SAME number
// as the design page (logo.inStorage) plus this order's own draw as open
// arithmetic (computeStorageBalance - pure, tested). Display-only, nothing is
// persisted, no extra API calls. Only DELIVERY quantities draw from storage;
// "to storage" quantities are production and count once NAV ships them, so
// they are shown as their own line instead of being added to the balance.
function StorageBalance({ item }: { item: ExtraLogo }) {
  const { t } = useTranslation();
  if (!enviroment.storageBalanceEnabled) return null;

  const balance = computeStorageBalance({
    inStorage: item.logo.inStorage,
    cartDelivery: item.cartDeliveryQuantity,
    extraQty: item.quantity,
    option: item.option,
  });
  if (!balance) return null;

  return (
    <div className="mt-2 border-t border-gray-200 pt-2 text-xs not-italic">
      <div className="flex flex-row justify-between gap-x-2">
        <span>{t("orderConfirmation.storageInStock")}</span>
        <span className="font-bold shrink-0">{balance.inStorage}</span>
      </div>
      <div className="flex flex-row justify-between gap-x-2">
        <span>{t("orderConfirmation.storageUsedByOrder")}</span>
        <span className="font-bold shrink-0">{balance.used}</span>
      </div>
      <div className={`flex flex-row justify-between gap-x-2${balance.after > 0 ? " text-status-success" : ""}`}>
        <span>{t("orderConfirmation.storageAfterOrder")}</span>
        <span className="font-bold shrink-0">{balance.after}</span>
      </div>
      {balance.addedAtShipment > 0 && (
        <div className="flex flex-row justify-between gap-x-2 text-gray-500">
          <span>{t("orderConfirmation.storageAddedAtShipment")}</span>
          <span className="font-bold shrink-0">+{balance.addedAtShipment}</span>
        </div>
      )}
    </div>
  );
}
